import { useCallback, useState } from 'react';

/** Where the dev-mode tweak values are kept between reloads. Shared with TweaksPanel. */
export const TWEAKS_STORAGE_KEY = 'mf-tweaks-v1';

/**
 * Dev-mode tweak values, persisted to localStorage.
 *
 * Lives apart from TweaksPanel purely so that file exports nothing but its component: a module
 * mixing a hook with a component loses Fast Refresh for both, which is what
 * react-refresh/only-export-components is warning about.
 */
export function useTweaks<T extends Record<string, unknown>>(
  defaults: T,
): [T, (key: string | Partial<T>, val?: unknown) => void] {
  const [values, setValues] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(TWEAKS_STORAGE_KEY);
      if (!raw) return defaults;
      const parsed: Record<string, unknown> = JSON.parse(raw) as Record<string, unknown>;
      // Only keep keys that exist in defaults - protects against schema drift.
      const merged = { ...defaults };
      (Object.keys(defaults) as (keyof T)[]).forEach(k => {
        if (k in parsed) (merged as Record<string, unknown>)[k as string] = parsed[k as string];
      });
      return merged;
    } catch {
      return defaults;
    }
  });

  const setTweak = useCallback((keyOrEdits: string | Partial<T>, val?: unknown) => {
    const edits: Partial<T> =
      typeof keyOrEdits === 'object' && keyOrEdits !== null
        ? keyOrEdits
        : ({ [keyOrEdits]: val } as Partial<T>);
    setValues(prev => {
      const next = { ...prev, ...edits };
      try { localStorage.setItem(TWEAKS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return [values, setTweak];
}
